import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { getSingleActiveCompetitionPath } from "@/features/competitions/redirect";
import { api, setAccessToken } from "@/lib/api";
import { clearSentryUser, setSentryUser } from "@/lib/sentry";

interface User {
  id: string;
  email: string;
  name: string;
  image?: string | null;
}

interface AuthState {
  user: User | null;
  isLoading: boolean;
  signOut: () => Promise<void>;
  getSessionToken: () => string | null;
  refreshSession: () => Promise<void>;
  setSession: (user: User, token: string) => void;
  getPrefetchedCompetitionPath: () => Promise<string | null> | null;
}

const AuthContext = createContext<AuthState>({
  user: null,
  isLoading: true,
  signOut: async () => {},
  getSessionToken: () => null,
  refreshSession: async () => {},
  setSession: () => {},
  getPrefetchedCompetitionPath: () => null,
});

function getStoredToken(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return localStorage.getItem("better-auth-session-token");
  } catch {
    return null;
  }
}

function storeToken(token: string | null) {
  if (typeof window === "undefined") return;
  try {
    if (token) {
      localStorage.setItem("better-auth-session-token", token);
    } else {
      localStorage.removeItem("better-auth-session-token");
    }
  } catch {
    /* noop */
  }
}

function getStoredUser(): User | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem("better-auth-cached-user");
    return raw ? (JSON.parse(raw) as User) : null;
  } catch {
    return null;
  }
}

function storeUser(user: User | null) {
  if (typeof window === "undefined") return;
  try {
    if (user) {
      localStorage.setItem("better-auth-cached-user", JSON.stringify(user));
    } else {
      localStorage.removeItem("better-auth-cached-user");
    }
  } catch {
    /* noop */
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(() => getStoredUser());
  // Trust a cached user optimistically — only block render when we have
  // a token but no cached identity to show while it's validated.
  const [isLoading, setIsLoading] = useState(() => !!getStoredToken() && !getStoredUser());
  const prefetchedCompetitionPath = useRef<Promise<string | null> | null>(null);

  const refreshSession = useCallback(async () => {
    const token = getStoredToken();
    if (!token) {
      setUser(null);
      storeUser(null);
      setAccessToken(null);
      setIsLoading(false);
      return;
    }
    setAccessToken(token);
    if (!getStoredUser()) {
      setIsLoading(true);
    }

    // Fire alongside session validation instead of after it resolves, so an
    // already-signed-in user landing on "/" doesn't pay for two sequential
    // round trips before AuthGuard can redirect them off the public page.
    if (
      typeof window !== "undefined" &&
      window.location.pathname === "/" &&
      !prefetchedCompetitionPath.current
    ) {
      prefetchedCompetitionPath.current = getSingleActiveCompetitionPath().catch(() => null);
    }

    try {
      const res = await api.get<{ user: User; session: { token: string } }>("/auth/session");
      if (res.data?.user) {
        setUser(res.data.user);
        storeUser(res.data.user);
        const newToken = res.data.session?.token ?? token;
        storeToken(newToken);
        setAccessToken(newToken);
      } else {
        storeToken(null);
        storeUser(null);
        setAccessToken(null);
        setUser(null);
      }
    } catch {
      setAccessToken(null);
      setUser(null);
      storeUser(null);
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (user) {
      setSentryUser({ id: user.id, email: user.email, name: user.name });
    } else if (!isLoading) {
      clearSentryUser();
    }
  }, [user, isLoading]);

  useEffect(() => {
    refreshSession();
  }, [refreshSession]);

  // Hydrates auth state straight from a session the caller already proved
  // valid (a sign-in/sign-up response's own user), instead of re-verifying
  // it through refreshSession's `/auth/session` round trip — that endpoint
  // does its own direct DB read and can lose a read-after-write race
  // against a session created moments earlier, wiping out the token this
  // call just confirmed was good.
  const setSession = useCallback((sessionUser: User, token: string) => {
    storeToken(token);
    storeUser(sessionUser);
    setAccessToken(token);
    setUser(sessionUser);
    setIsLoading(false);
  }, []);

  const value = useMemo(
    () => ({
      user,
      isLoading,
      signOut: async () => {
        await api.post("/auth/sign-out").catch(() => {});
        storeToken(null);
        storeUser(null);
        setAccessToken(null);
        setUser(null);
      },
      getSessionToken: getStoredToken,
      refreshSession,
      setSession,
      getPrefetchedCompetitionPath: () => prefetchedCompetitionPath.current,
    }),
    [user, isLoading, refreshSession, setSession],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
